import { useQueryClient } from "@tanstack/react-query";
import { forwardRef, useMemo, useState } from "react";

import AutocompleteSelect from "@/common/components/Autocomplete/AutocompleteSelect";
import { UserDisplay } from "@/common/components/UserDisplay";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { commonQueryKeys } from "@/common/data/commonQueryKeys";
import { useUser, useUsersSearch } from "@/common/data/useUsers";
import { useDebounce } from "@/common/hooks/useDebounce";
import {
  AutocompleteImplementationProps,
  AutocompleteValueIdProps,
} from "@/common/utils/autocomplete";
import { SEARCH_DEBOUNCE_MS } from "@reflag/shared/constants";
import { UserName, UsersBody } from "@reflag/shared/userAPI";

export type UserAutocompleteSelectProps = {
  envId?: string;
} & AutocompleteImplementationProps<UserName> &
  AutocompleteValueIdProps<UserName> & {
    query?: Omit<UsersBody, "envId">;
  };

export const UserAutocompleteSelect = forwardRef<
  HTMLButtonElement,
  UserAutocompleteSelectProps
>(function UserAutocompleteSelect(
  {
    valueAsId,
    value: valueProp,
    query,
    envId: envIdOverride,
    placeholder = "Select a user",
    canClear,
    onChange,
    ...autocompleteProps
  }: UserAutocompleteSelectProps,
  ref,
) {
  const { appId, envId: currentEnvId } = useEnvironment();
  const envId = envIdOverride ?? currentEnvId;
  const valueId = useMemo(
    () => (valueAsId ? valueProp : valueProp?.id),
    [valueAsId, valueProp],
  );
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, SEARCH_DEBOUNCE_MS);

  const { data: valueEnhancement, isLoading: isLoadingEnhancement } = useUser(
    valueId ?? undefined,
    envId,
  );

  const {
    data: userData = [],
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useUsersSearch(debouncedSearchValue, query, undefined, envId);

  const internalValue = useMemo(() => {
    return valueProp && valueProp === valueEnhancement?.id
      ? valueEnhancement
      : undefined;
  }, [valueProp, valueEnhancement]);

  const suggestions = useMemo(() => {
    return internalValue
      ? [internalValue, ...userData.filter((u) => u.id !== internalValue.id)]
      : userData;
  }, [userData, internalValue]);

  return (
    <AutocompleteSelect
      ref={ref}
      canClear={canClear}
      hasMore={hasNextPage}
      isLoading={isLoading || isLoadingEnhancement}
      isLoadingMore={isFetchingNextPage}
      isLoadingSuggestions={isFetching}
      itemKeyProperty="id"
      itemRenderFn={(u, search) => (
        <UserDisplay py={0.5} search={search} user={u} showEmail showId />
      )}
      loadingText="Loading..."
      placeholder={placeholder}
      suggestions={suggestions}
      value={internalValue}
      onChange={(newValue: UserName | null) => {
        setSearchValue("");
        if (!newValue) {
          if (canClear) onChange?.(null);
          return;
        }
        // If a value is selected, pre-seed the single user query for immediate display
        queryClient.setQueryData(
          commonQueryKeys.user(appId, envId, newValue.id),
          newValue,
        );
        // If valueAsId is true, return only the id of the selected user
        if (valueAsId) {
          onChange?.(newValue?.id ?? null);
        } else {
          onChange?.(newValue ?? null);
        }
      }}
      onClear={() => {
        setSearchValue("");
      }}
      onLoadMore={fetchNextPage}
      onSearchInput={setSearchValue}
      {...autocompleteProps}
    />
  );
});
